import { apiUrl } from "utils/apiUrl";
import { Col, Form, Input, InputNumber, message, Modal, Row, Tabs, Tag } from "antd";
import { Rule } from "antd/lib/form";
import { customerApi } from "api/customer.api";
import { partnerApi } from "api/partner.api";
import { staffApi } from "api/staff.api";
import { AppButton, AppSelect } from "components/antd-ui";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { userStore } from "store/userStore";
import { Customer } from "types/customer";
import { ModalStatus } from "types/modal";
import { PartnerOption } from "types/partner";
import { Staff } from "types/staff";
import { can } from "utils/auth";
import { $url } from "utils/url";
import "../Customer.scss";
import { NoteLog } from "components/Note/NoteLog";
import { NoteRefType } from "types/note";
import {
  customerIndustryOptions,
  customerSourceOptions,
  getCustomerSourceLabel,
  getCustomerTagColor,
} from "../customer.meta";
import { CustomerDebtTab } from "./CustomerDebtTab";
import { CustomerOpportunityTab } from "./CustomerOpportunityTab";
import { CustomerOrderTab } from "./CustomerOrderTab";
import { CustomerRejectModal } from "./CustomerRejectModal";
import { getCustomerStage, getRejectReasonLabel } from "config/customerStage";
import { CustomerQuoteTab } from "./CustomerQuoteTab";

const customerPath = "/customer";
const uploadUrl = apiUrl("/v1/admin/staff/upload");
const nameRules: Rule[] = [{ required: true, message: "Bắt buộc" }];
const staffRules: Rule[] = [{ required: true, message: "Bắt buộc" }];
const emailRules: Rule[] = [{ type: "email", message: "Email không hợp lệ" }];

type StaffOption = { label: string; value: number };

interface CustomerForm {
  name: string;
  code?: string;
  taxCode?: string;
  address?: string;
  source?: string;
  industry?: string;
  contactIntervalDays?: number | null;
  tags?: string[];
  staffId?: number;
  partnerId?: number;
  contactName1?: string;
  contactPhone1?: string;
  contactEmail1?: string;
  contactAvatar1?: string;
  contactPosition1?: string;
  contactName2?: string;
  contactPhone2?: string;
  contactEmail2?: string;
  contactAvatar2?: string;
  contactPosition2?: string;
}

export interface CustomerModal {
  handleCreate: () => void;
  handleUpdate: (customer: Customer) => void;
  handleView: (customer: Customer) => void;
}

interface CustomerModalProps {
  onClose: () => void;
  onSubmitOk: (customer?: Customer) => void;
}

const display = (value?: string) => value?.trim() || "--";

const toForm = (customer: Customer): CustomerForm => ({
  name: customer.name,
  code: customer.code,
  taxCode: customer.taxCode,
  address: customer.address,
  source: customer.source,
  industry: customer.industry,
  contactIntervalDays: customer.contactIntervalDays ?? null,
  tags: customer.tags || [],
  staffId: customer.staff?.id,
  partnerId: customer.partner?.id,
  contactName1: customer.contactName1,
  contactPhone1: customer.contactPhone1,
  contactEmail1: customer.contactEmail1,
  contactAvatar1: customer.contactAvatar1,
  contactPosition1: customer.contactPosition1,
  contactName2: customer.contactName2,
  contactPhone2: customer.contactPhone2,
  contactEmail2: customer.contactEmail2,
  contactAvatar2: customer.contactAvatar2,
  contactPosition2: customer.contactPosition2,
});

const CustomerDetail = ({ customer }: { customer: Customer }) => {
  const contacts = [
    {
      title: "Người liên hệ 1",
      name: customer.contactName1,
      phone: customer.contactPhone1,
      email: customer.contactEmail1,
      avatar: customer.contactAvatar1,
      position: customer.contactPosition1,
    },
    {
      title: "Người liên hệ 2",
      name: customer.contactName2,
      phone: customer.contactPhone2,
      email: customer.contactEmail2,
      avatar: customer.contactAvatar2,
      position: customer.contactPosition2,
    },
  ];

  return (
    <div>
      <div className="customerModal__section">
        <div className="customerModal__title">Thông tin khách hàng</div>
        <div className="customerModal__grid">
          <span className="customerModal__label">Tên</span>
          <span>{display(customer.name)}</span>
          <span className="customerModal__label">Mã</span>
          <span>{display(customer.code)}</span>
          <span className="customerModal__label">Mã số thuế</span>
          <span>{display(customer.taxCode)}</span>
          <span className="customerModal__label">Địa chỉ</span>
          <span>{display(customer.address)}</span>
          <span className="customerModal__label">Nguồn</span>
          <span>{display(getCustomerSourceLabel(customer.source))}</span>
          <span className="customerModal__label">Ngành nghề</span>
          <span>{display(customer.industry)}</span>
          <span className="customerModal__label">Tags</span>
          <span>
            {customer.tags?.length
              ? customer.tags.map((tag) => (
                <Tag key={tag} color={getCustomerTagColor(tag)}>
                  {tag}
                </Tag>
              ))
              : "--"}
          </span>
          <span className="customerModal__label">Phân loại</span>
          <span>
            {getCustomerStage(customer.stage) ? (
              <Tag color={getCustomerStage(customer.stage)?.color}>{getCustomerStage(customer.stage)?.label}</Tag>
            ) : (
              "--"
            )}
            {customer.stage === "REJECTED" && (
              <span>
                Lý do: {getRejectReasonLabel(customer.rejectCode)}
                {customer.rejectedAt && customer.rejectNote ? ` - ${customer.rejectNote}` : ""}
              </span>
            )}
          </span>
          <span className="customerModal__label">Sale chăm sóc</span>
          <span>{display(customer.staff?.name)}</span>
          <span className="customerModal__label">Đối tác giới thiệu</span>
          <span>{display(customer.partner?.name)}</span>
        </div>
      </div>

      <div className="customerModal__contacts">
        {contacts.map((contact) => (
          <div key={contact.title} className="customerModal__section">
            <div className="customerModal__title">{contact.title}</div>
            <div className="customerModal__contact">
              {contact.avatar ? (
                <img className="customerModal__avatar" src={$url(contact.avatar)} alt="" />
              ) : (
                <div className="customerModal__avatar customerModal__avatar--empty">--</div>
              )}
              <div className="customerModal__grid">
                <span className="customerModal__label">Tên</span>
                <span>{display(contact.name)}</span>
                <span className="customerModal__label">Số điện thoại</span>
                <span>{display(contact.phone)}</span>
                <span className="customerModal__label">Email</span>
                <span>{display(contact.email)}</span>
                <span className="customerModal__label">Chức vụ</span>
                <span>{display(contact.position)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CustomerModal = observer(
  React.forwardRef(({ onClose, onSubmitOk }: CustomerModalProps, ref) => {
    const [form] = Form.useForm<CustomerForm>();
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [status, setStatus] = useState<ModalStatus>("create");
    const [selectedCustomer, setSelectedCustomer] = useState<Customer>();
    const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
    const [partnerOptions, setPartnerOptions] = useState<StaffOption[]>([]);
    const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);
    const [activeTab, setActiveTab] = useState("info");
    const [rejectTarget, setRejectTarget] = useState<Customer>();

    const onlyMine = !userStore.info.role?.isAdmin && can(customerPath, "ONLY_MINE");
    const canUpdate = can(customerPath, "UPDATE");

    // Gợi ý tag đã dùng: tải khi mở popup
    useEffect(() => {
      if (!visible) {
        return;
      }
      customerApi.findTags().then((res) => {
        setTagOptions(((res.data.tags || []) as { name: string }[]).map((tag) => ({ label: tag.name, value: tag.name })));
      });
    }, [visible]);

    const fetchStaff = useCallback(
      async (keyword: string) => {
        const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword });
        const staffs: Staff[] = res.data.staffs || [];
        setStaffOptions((prev) => {
          const selectedId = form.getFieldValue("staffId");
          const selected = prev.find((option) => option.value === selectedId);
          const options = staffs.map((staff) => ({
            label: staff.name || staff.username,
            value: staff.id,
          }));
          if (selected && !options.some((option) => option.value === selected.value)) {
            return [selected, ...options];
          }
          return options;
        });
      },
      [form],
    );

    const fetchPartners = useCallback(
      async (keyword: string) => {
        const res = await partnerApi.findOptions({ search: keyword });
        const partners: PartnerOption[] = res.data.partners || [];
        setPartnerOptions((prev) => {
          const selectedId = form.getFieldValue("partnerId");
          const selected = prev.find((option) => option.value === selectedId);
          const options = partners.map((partner) => ({
            label: partner.company ? `${partner.name} - ${partner.company}` : partner.name,
            value: partner.id,
          }));
          if (selected && !options.some((option) => option.value === selected.value)) {
            return [selected, ...options];
          }
          return options;
        });
      },
      [form],
    );

    const debounceSearchPartner = useMemo(
      () =>
        debounce((keyword: string) => {
          fetchPartners(keyword);
        }, 500),
      [fetchPartners],
    );

    useEffect(() => {
      return () => {
        debounceSearchPartner.cancel();
      };
    }, [debounceSearchPartner]);

    useEffect(() => {
      if (!visible || status === "view") {
        return;
      }
      fetchPartners("");
    }, [visible, status, fetchPartners]);

    const debounceSearchStaff = useMemo(
      () =>
        debounce((keyword: string) => {
          fetchStaff(keyword);
        }, 500),
      [fetchStaff],
    );

    useEffect(() => {
      return () => {
        debounceSearchStaff.cancel();
      };
    }, [debounceSearchStaff]);

    useEffect(() => {
      if (!visible || onlyMine || status === "view") {
        return;
      }
      fetchStaff("");
    }, [visible, onlyMine, status, fetchStaff]);

    const fillCustomer = useCallback(
      (customer: Customer) => {
        form.setFieldsValue(toForm(customer));
        if (customer.staff) {
          setStaffOptions([
            {
              label: customer.staff.name || customer.staff.username,
              value: customer.staff.id,
            },
          ]);
        } else {
          setStaffOptions([]);
        }
        setPartnerOptions(
          customer.partner
            ? [
              {
                label: customer.partner.company ? `${customer.partner.name} - ${customer.partner.company}` : customer.partner.name,
                value: customer.partner.id,
              },
            ]
            : [],
        );
        setSelectedCustomer(customer);
      },
      [form],
    );

    useImperativeHandle<any, CustomerModal>(
      ref,
      () => ({
        handleCreate() {
          form.resetFields();
          setStaffOptions([]);
          setPartnerOptions([]);
          setSelectedCustomer(undefined);
          setStatus("create");
          setVisible(true);
        },
        handleUpdate(customer: Customer) {
          fillCustomer(customer);
          setStatus("update");
          setVisible(true);
        },
        handleView(customer: Customer) {
          fillCustomer(customer);
          setActiveTab("info");
          setStatus("view");
          setVisible(true);
        },
      }),
      [fillCustomer, form],
    );

    const handleClose = useCallback(() => {
      setVisible(false);
      setSelectedCustomer(undefined);
      onClose();
    }, [onClose]);

    const handleRejectDone = useCallback(async () => {
      const customerId = rejectTarget?.id;
      setRejectTarget(undefined);
      if (!customerId) return;
      const res = await customerApi.findOne(customerId);
      fillCustomer(res.data as Customer);
      onSubmitOk();
    }, [fillCustomer, onSubmitOk, rejectTarget?.id]);

    const handleUnreject = useCallback(async () => {
      if (!selectedCustomer) return;
      await customerApi.unreject(selectedCustomer.id);
      message.success("Đã bỏ đánh dấu từ chối");
      const res = await customerApi.findOne(selectedCustomer.id);
      fillCustomer(res.data as Customer);
      onSubmitOk();
    }, [fillCustomer, onSubmitOk, selectedCustomer]);

    const handleStartEdit = useCallback(() => {
      setActiveTab("info");
      setStatus("update");
    }, []);

    const handleAvatar1 = useCallback(
      (path: string) => {
        form.setFieldsValue({ contactAvatar1: path });
      },
      [form],
    );

    const handleAvatar2 = useCallback(
      (path: string) => {
        form.setFieldsValue({ contactAvatar2: path });
      },
      [form],
    );

    const buildPayload = useCallback(() => {
      const values = form.getFieldsValue();
      return {
        customer: {
          name: values.name || "",
          code: values.code || "",
          taxCode: (values.taxCode || "").trim(),
          address: values.address || "",
          source: (values.source || "").trim(),
          industry: (values.industry || "").trim(),
          // null = dùng chu kỳ mặc định ở menu Cấu hình
          contactIntervalDays: values.contactIntervalDays ?? null,
          contactName1: values.contactName1 || "",
          contactPhone1: values.contactPhone1 || "",
          contactEmail1: values.contactEmail1 || "",
          contactAvatar1: values.contactAvatar1 || "",
          contactPosition1: values.contactPosition1 || "",
          contactName2: values.contactName2 || "",
          contactPhone2: values.contactPhone2 || "",
          contactEmail2: values.contactEmail2 || "",
          contactAvatar2: values.contactAvatar2 || "",
          contactPosition2: values.contactPosition2 || "",
        },
        tags: values.tags || [],
        staffId: onlyMine ? undefined : values.staffId,
        // 0 = bỏ đối tác giới thiệu
        partnerId: values.partnerId ?? 0,
      };
    }, [form, onlyMine]);

    const handleSubmit = useCallback(async () => {
      await form.validateFields();
      const data = buildPayload();
      setLoading(true);
      try {
        const res =
          status === "create"
            ? await customerApi.create(data)
            : await customerApi.update(selectedCustomer?.id || 0, data);
        message.success(status === "create" ? "Tạo mới thành công!" : "Cập nhật thành công!");
        handleClose();
        onSubmitOk(res.data);
      } finally {
        setLoading(false);
      }
    }, [buildPayload, form, handleClose, onSubmitOk, selectedCustomer?.id, status]);

    const title =
      status === "create" ? "Tạo mới khách hàng" : status === "update" ? "Cập nhật khách hàng" : "Xem khách hàng";

    return (
      <Modal
        className="customerModal"
        onCancel={handleClose}
        open={visible}
        title={title}
        style={{ top: 20 }}
        width={1200}
        confirmLoading={loading}
        footer={
          status === "view" ? (
            <div className="customerModal__footer">
              <AppButton onClick={handleClose}>Đóng</AppButton>
              {canUpdate && selectedCustomer && selectedCustomer.stage !== "SOLD" && (
                selectedCustomer.rejectedAt ? (
                  <AppButton onClick={handleUnreject}>Bỏ từ chối</AppButton>
                ) : (
                  <AppButton danger onClick={() => setRejectTarget(selectedCustomer)}>
                    Từ chối
                  </AppButton>
                )
              )}
              {canUpdate && (
                <AppButton tone="primary" onClick={handleStartEdit}>
                  Sửa
                </AppButton>
              )}
            </div>
          ) : (
            <div className="customerModal__footer">
              <AppButton onClick={handleClose}>Hủy</AppButton>
              <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
                Lưu
              </AppButton>
            </div>
          )
        }
      >
        {status === "view" && selectedCustomer ? (
          <Tabs
            key={selectedCustomer.id}
            className="customerModal__tabs"
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: "info", label: "Thông tin khách hàng", children: <CustomerDetail customer={selectedCustomer} /> },
              { key: "opportunity", label: "Cơ hội", children: <CustomerOpportunityTab customerId={selectedCustomer.id} /> },
              { key: "quote", label: "Báo giá", children: <CustomerQuoteTab customerId={selectedCustomer.id} /> },
              { key: "order", label: "Đơn hàng", children: <CustomerOrderTab customerId={selectedCustomer.id} /> },
              { key: "debt", label: "Công nợ", children: <CustomerDebtTab customerId={selectedCustomer.id} /> },
              { key: "note", label: "Ghi chú", children: <NoteLog refType={NoteRefType.Customer} refId={selectedCustomer.id} /> },
            ]}
          />
        ) : (
          <Form
            className="customerModal__form"
            layout="horizontal"
            labelAlign="left"
            labelCol={{ flex: "140px" }}
            colon={false}
            form={form}
          >
            <div className="customerModal__section">
            <div className="customerModal__title">Thông tin khách hàng</div>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item label="Tên" name="name" rules={nameRules}>
                  <Input placeholder="" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Mã" name="code">
                  <Input placeholder="Để trống để tự sinh mã" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Mã số thuế" name="taxCode">
                  <Input placeholder="" />
                </Form.Item>
              </Col>
              <Col span={12}>
                {onlyMine ? (
                  <Form.Item label="Sale chăm sóc">
                    <Input value={userStore.info.name || ""} disabled />
                  </Form.Item>
                ) : (
                  <Form.Item label="Sale chăm sóc" name="staffId" rules={staffRules}>
                    <AppSelect
                      showSearch
                      filterOption={false}
                      options={staffOptions}
                      onSearch={debounceSearchStaff}
                      placeholder="Chọn nhân viên"
                    />
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Nguồn"
                  name="source"
                  getValueProps={(value?: string) => ({ value: value ? [value] : [] })}
                  normalize={(value?: string[]) => value?.[value.length - 1] || ""}
                >
                  <AppSelect
                    mode="tags"
                    maxCount={1}
                    allowClear
                    options={customerSourceOptions}
                    placeholder="Chọn hoặc nhập nguồn"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Ngành nghề"
                  name="industry"
                  getValueProps={(value?: string) => ({ value: value ? [value] : [] })}
                  normalize={(value?: string[]) => value?.[value.length - 1] || ""}
                >
                  <AppSelect
                    mode="tags"
                    maxCount={1}
                    allowClear
                    options={customerIndustryOptions}
                    placeholder="Chọn hoặc nhập ngành nghề"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Chu kỳ tương tác"
                  name="contactIntervalDays"
                  tooltip="Số ngày cần tương tác với khách này (1-30). Để trống = dùng mặc định ở menu Cấu hình"
                >
                  <InputNumber min={1} max={30} precision={0} style={{ width: "100%" }} addonAfter="ngày" placeholder="Mặc định" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Đối tác giới thiệu" name="partnerId">
                  <AppSelect
                    showSearch
                    allowClear
                    filterOption={false}
                    options={partnerOptions}
                    onSearch={debounceSearchPartner}
                    placeholder="Chọn đối tác (nếu có)"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Tags" name="tags">
                  <AppSelect
                    mode="tags"
                    allowClear
                    tokenSeparators={[","]}
                    options={tagOptions}
                    placeholder="Chọn hoặc gõ tag mới (VD: ưu tiên, khó tính)"
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Địa chỉ" name="address">
                  <Input placeholder="" />
                </Form.Item>
              </Col>
            </Row>
            </div>

            <div className="customerModal__contacts">
              {[
                { n: 1 as const, onUploadOk: handleAvatar1 },
                { n: 2 as const, onUploadOk: handleAvatar2 },
              ].map(({ n, onUploadOk }) => (
                <div key={n} className="customerModal__section">
                  <div className="customerModal__title">Người liên hệ {n}</div>
                  <div className="customerModal__contact customerModal__contact--form">
                    <Form.Item noStyle shouldUpdate>
                      {() => (
                        <Form.Item name={`contactAvatar${n}`} noStyle>
                          <SingleImageUpload
                            onUploadOk={onUploadOk}
                            imageUrl={form.getFieldValue(`contactAvatar${n}`)}
                            uploadUrl={uploadUrl}
                            width={72}
                            height={72}
                          />
                        </Form.Item>
                      )}
                    </Form.Item>
                    <div className="customerModal__formFields">
                      <Form.Item label="Tên" name={`contactName${n}`} labelCol={{ flex: "110px" }}>
                        <Input placeholder="" />
                      </Form.Item>
                      <Form.Item label="Số điện thoại" name={`contactPhone${n}`} labelCol={{ flex: "110px" }}>
                        <Input placeholder="" />
                      </Form.Item>
                      <Form.Item label="Email" name={`contactEmail${n}`} rules={emailRules} labelCol={{ flex: "110px" }}>
                        <Input placeholder="" />
                      </Form.Item>
                      <Form.Item label="Chức vụ" name={`contactPosition${n}`} labelCol={{ flex: "110px" }}>
                        <Input placeholder="" />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Form>
        )}
        <CustomerRejectModal customer={rejectTarget} onClose={() => setRejectTarget(undefined)} onDone={handleRejectDone} />
      </Modal>
    );
  }),
);
